import React, { useState, useRef } from "react";
import { Box, Typography, IconButton, Modal } from "@mui/material";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import ReactPlayer from "react-player";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
// Iconsax — ClusterDen's standard icon library (see [[icon-library-iconsax]]); Play replaces
// the MUI PlayArrow overlay on video grid cells, Gallery is the broken-image fallback
// (Rahim, 2026-08-27, "proper spacing padding and everything").
import { Play, Gallery } from "iconsax-react";

function MediaCollage({ media, fromMe = false, mediaType = "image" }) {
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [mediaImageError, setMediaImageError] = useState({});

  const playerRef = useRef(null);

  const handleMediaClick = (index) => {
    setSelectedMediaIndex(index);
    setIsModalOpen(true);
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsPlaying(false);
  };

  const handlePreviousMedia = () => {
    setSelectedMediaIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleNextMedia = () => {
    setSelectedMediaIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      handlePreviousMedia();
    } else if (event.key === "ArrowRight") {
      handleNextMedia();
    } else if (event.key === "Escape") {
      handleCloseModal();
    }
  };

  const handlePlayPause = () => {
    setIsPlaying((prev) => !prev);
  };

  const handleProgress = (state) => {
    setCurrentTime(state.playedSeconds);
  };

  const handleDuration = (duration) => {
    setDuration(duration);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // Determine grid layout based on number of media items
  const getGridLayout = () => {
    if (media.length <= 4) {
      return {
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: media.length <= 2 ? "1fr" : "1fr 1fr",
      };
    } else {
      return {
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr 1fr",
      };
    }
  };

  const getMediaStyle = (index) => {
    const baseStyle = {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      cursor: "pointer",
      borderRadius: "8px",
    };

    // For 5+ media items, show only first 4 in grid
    if (media.length > 4 && index >= 3) {
      return {
        ...baseStyle,
        position: "relative",
      };
    }

    return baseStyle;
  };

  const isVideo = (mediaItem) => {
    if (mediaType === "video") return true;
    if (mediaItem instanceof File) {
      return mediaItem.type.startsWith('video/');
    }
    // Check if URL ends with video extensions
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'];
    return videoExtensions.some(ext => mediaItem.toLowerCase().includes(ext));
  };

  const renderMedia = (mediaItem, index) => {
    const isLocalFile = mediaItem instanceof File;
    const displayUrl = isLocalFile ? URL.createObjectURL(mediaItem) : mediaItem;
    const isVideoItem = isVideo(mediaItem);

    return (
      <Box
        key={index}
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: "8px",
          backgroundColor: isVideoItem ? "#000" : "transparent",
          cursor: "pointer",
        }}
        onClick={() => handleMediaClick(index)}
      >
        {isVideoItem ? (
          // Real first-frame thumbnail instead of a flat dark box (Rahim, 2026-08-27: "make
          // it like the image one") — browsers paint a <video>'s first frame automatically
          // once metadata loads, same as a poster image, no extra work needed. Play icon
          // overlay (below) still sits on top to signal it's a video, not a photo.
          <Box
            component="video"
            src={displayUrl}
            muted
            playsInline
            preload="metadata"
            sx={{ ...getMediaStyle(index), backgroundColor: "#1a1a1a" }}
          />
        ) : (
          // Real photo directly in the grid cell — this used to ONLY ever render a real image
          // for Cloudinary URLs (via the blur-placeholder trick above); every other image type
          // fell straight to a generic icon box regardless of whether the URL actually worked,
          // which is why a genuinely valid photo still showed as a broken-looking placeholder
          // (Rahim, 2026-08-27, found via a real screenshot — 3 identical grey icon tiles).
          // `onError` still falls back to a muted icon tile for a URL that's truly broken.
          !mediaImageError[index] ? (
            <Box
              component="img"
              src={displayUrl}
              alt={`chat-media-${index}`}
              onError={() => setMediaImageError((prev) => ({ ...prev, [index]: true }))}
              sx={{ ...getMediaStyle(index), display: "block", color: "transparent" }}
            />
          ) : (
            <Box
              sx={{
                ...getMediaStyle(index),
                backgroundColor: "#F0F2F5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Gallery size={28} color="#8A8D91" variant="Linear" />
            </Box>
          )
        )}
        
        {/* Play button overlay for videos */}
        {isVideoItem && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
            }}
          >
            <IconButton
              sx={{
                color: "#fff",
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                padding: "8px",
              }}
            >
              <Play size={16} color="currentColor" variant="Bold" />
            </IconButton>
          </Box>
        )}

        {/* Show overlay with count for the 4th position when there are 5+ media items */}
        {media.length > 4 && index === 3 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: "white",
                fontWeight: "bold",
                textShadow: "2px 2px 4px rgba(0,0,0,0.8)",
              }}
            >
              +{media.length - 4}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderModalContent = () => {
    const selectedMedia = media[selectedMediaIndex];
    const isLocalFile = selectedMedia instanceof File;
    const displayUrl = isLocalFile ? URL.createObjectURL(selectedMedia) : selectedMedia;
    const isVideoItem = isVideo(selectedMedia);

    if (isVideoItem) {
      return (
        <Box
          sx={{
            maxWidth: "90vw",
            maxHeight: "90vh",
            position: "relative",
          }}
        >
          <ReactPlayer
            ref={playerRef}
            url={displayUrl}
            playing={isPlaying}
            width="100%"
            height="100%"
            onProgress={handleProgress}
            onDuration={handleDuration}
            controls={false}
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          />
          
          {/* Time display */}
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              bottom: 16,
              left: 16,
              color: "white",
              zIndex: 10,
            }}
          >
            {formatTime(currentTime)} / {formatTime(duration)}
          </Typography>

          {/* Play/Pause button */}
          <IconButton
            onClick={handlePlayPause}
            sx={{
              position: "absolute",
              bottom: 16,
              right: 16,
              color: "white",
              zIndex: 10,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
            }}
          >
            <Play size={22} color="currentColor" variant="Bold" />
          </IconButton>
        </Box>
      );
    } else {
      return (
        <PhotoProvider>
          <PhotoView src={displayUrl}>
            <img
              src={displayUrl}
              alt={`fullscreen-${selectedMediaIndex}`}
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          </PhotoView>
        </PhotoProvider>
      );
    }
  };

  return (
    <>
      {/* Real card wrapper + committed width instead of a bare floating grid (Rahim,
          2026-08-27: "proper spacing padding and everything") — without an explicit
          width/minWidth this grid was subject to the exact same shrink-wrap-ancestor
          collapse the location card had (ChatCard's outer pill bubble has no dedicated
          maxWidth case for imageCollage/videoCollage either), which is why the grid cells
          rendered tiny regardless of `maxWidth`. */}
      <Box
        sx={{
          borderRadius: "14px",
          // Mode-aware, not fixed dark (Rahim, 2026-08-27, correcting the earlier fixed-
          // dark pass: "in light mode keep it white but in dark keep it black") — white
          // card in light mode (original), dark chrome (#1a1a1a, same shade already used
          // for this file's own video-item background below) only in dark mode.
          border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#2E2E2E" : "#DADCDE"}`,
          bgcolor: (theme) => (theme.palette.mode === "dark" ? "#1a1a1a" : "#FFFFFF"),
          padding: "8px",
          width: "100%",
          // 260px read too small once actually sized correctly — Rahim: "increase size"
          // (2026-08-27), back up partway between that and the original oversized version.
          minWidth: { xs: 0, sm: 260 },
          maxWidth: "min(360px, calc(var(--chatbox-width, 100vw) * 0.55))",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gap: "6px",
            maxHeight: "300px",
            ...getGridLayout(),
          }}
        >
          {media.slice(0, Math.min(4, media.length)).map((mediaItem, index) =>
            renderMedia(mediaItem, index)
          )}
        </Box>
      </Box>

      {/* Full-screen Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        onKeyDown={handleKeyDown}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white",
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Navigation arrows */}
          {media.length > 1 && (
            <>
              <IconButton
                onClick={handlePreviousMedia}
                sx={{
                  position: "absolute",
                  left: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  zIndex: 10,
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 40 }} />
              </IconButton>
              <IconButton
                onClick={handleNextMedia}
                sx={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  zIndex: 10,
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 40 }} />
              </IconButton>
            </>
          )}

          {/* Media counter */}
          {media.length > 1 && (
            <Typography
              variant="body1"
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                color: "white",
                zIndex: 10,
              }}
            >
              {selectedMediaIndex + 1} / {media.length}
            </Typography>
          )}

          {/* Main media content */}
          {renderModalContent()}
        </Box>
      </Modal>
    </>
  );
}

export default MediaCollage;
